import moment from 'moment';
import store from '../../store';
import Janitor from '../Janitor';
import Analytics from '../analytics';
import ModalManager from '../modalManager';
import stateManager from '../user/stateManager';
import AutoCheckoutEducationModal from '../../components/provider/checkout/AutoCheckoutEducationModal';
import { fetchPaymentSettings } from './PaymentSettings';
import { loadProvider } from '../Api';

/**
 * This module contains logic for showing the
 * snackbars that open the auto checkout education modal
 */

export const AUTO_CHECKOUT_SNACKBAR_LAST_DIMISSED_DATE = 'autocheckout-snackbar-last-dismissed-date';
const janitor = new Janitor();

/**
 * Mark a snack bar dismissed on User State
 */
const markSnackbarDismissed = () => {
  stateManager.addAction(AUTO_CHECKOUT_SNACKBAR_LAST_DIMISSED_DATE, moment().format());
};

/**
 * Shows the autocheckout education modal
 */
const showAutoCheckoutEducation = () => {
  ModalManager.openModal(AutoCheckoutEducationModal, {
    onCloseModal: () => {
      markSnackbarDismissed();
    },
  });
};

/**
 * Props for autocheckout snackbar
 * @type {Object}
 */
const autoCheckoutSnackbarProps = {
  header: 'Reminder: Your clients will be charged automatically one hour after their appointment.',
  confirm: {
    text: 'Learn More',
    action: () => {
      Analytics.track('pro_learn_autocheckout_view', {
        source: 'autocheckout_snackbar',
      });
      showAutoCheckoutEducation();
    },
  },

  dismiss: {
    text: 'Dismiss',
    action: () => {
      markSnackbarDismissed();
      janitor.cleanup();
      Analytics.track('pro_learn_autocheckout_snackbar_dismiss_clicked');
    },
  },
};

const AutoCheckoutSnackBar = {
  openAutoCheckoutSnackBar(snackBarProps) {
    store.dispatch.snackbar.open(snackBarProps);
  },

  showSnackBars(providerId) {
    janitor.addPromise(Promise.all([
      loadProvider(providerId),
      stateManager.getActionValue(AUTO_CHECKOUT_SNACKBAR_LAST_DIMISSED_DATE),
    ])).then(([provider, lastDismissedDate]) => {
      // if pro is not on autocheckout do not ever show snackbar
      if (!provider.autocheckout_enabled) {
        return;
      }

      // we only want to show the the snackbar to:
      // Pro's who are autocheckout enabled and hasn't dismissed the snackbar
      // or Pro's who have dismissed the snackbar and it has already been a day
      // since they last dismissed it
      if (
        (provider.autocheckout_enabled && !lastDismissedDate)
          || moment().isAfter(lastDismissedDate, 'days')
      ) {
        janitor.addPromise(fetchPaymentSettings(providerId))
          .then(paymentSettings => {
            const {
              autocheckout_enabled_time: autoCheckoutEnabledTime,
            } = paymentSettings;
            // if autocheckout enabled time is within 3 days from when it was enabled
            // we want to show the snackbar. We only want to compare if it's start of day
            const autoCheckoutEnabledStartOfDay = moment(autoCheckoutEnabledTime).startOf('day');
            const startOfDayToday = moment().startOf('day');
            if (
              startOfDayToday.diff(autoCheckoutEnabledStartOfDay, 'days') <= 2
            ) {
              Analytics.track('pro_autocheckout_snackbar_view');
              this.openAutoCheckoutSnackBar(autoCheckoutSnackbarProps);
            } else {
              // if we reach this point, either it's been past 3 days since autocheckout was enabled
              // we set the dismissed date to autocheckout enabled time to ensure
              // we don't ever show the snackbar again
              // unless they switch from Manual then back to Auto
              stateManager.addAction(
                AUTO_CHECKOUT_SNACKBAR_LAST_DIMISSED_DATE,
                autoCheckoutEnabledTime,
              );
            }
          });
      }
    });
  },
};

export default AutoCheckoutSnackBar;
